/**
 * Case Converter Tool
 * 
 * Converts text between different cases.
 */

import {
  camelCase,
  capitalCase,
  constantCase,
  dotCase,
  kebabCase,
  pascalCase,
  sentenceCase,
  snakeCase,
} from 'change-case';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const CaseConverterTool: ITransformerTool = {
  id: 'case-converter',
  name: 'Case Converter',
  description: 'Convert text between different cases',
  version: '1.0.0',
  type: 'transformer',
  category: 'Text Tools',
  keywords: ['case', 'convert', 'uppercase', 'lowercase', 'camel', 'snake', 'text'],

  seo: {
    slug: 'case-converter',
    pageTitle: 'Case Converter - Free Online Text Case Tool',
    metaDescription: 'Convert text between uppercase, lowercase, camelCase, snake_case, kebab-case, and more. Fast, private, runs entirely in your browser.',
    keywords: ['case converter', 'text case tool', 'uppercase converter', 'camelcase converter', 'snake case'],
    relatedTools: ['find-replace', 'sort-lines'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'caseType',
      type: 'select',
      label: 'Case Type',
      defaultValue: 'lowercase',
      options: [
        'lowercase',
        'UPPERCASE',
        'Title Case',
        'Sentence case',
        'camelCase',
        'PascalCase',
        'snake_case',
        'SCREAMING_SNAKE_CASE',
        'kebab-case',
        'dot.case',
      ],
      description: 'Target case format',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const caseType = (config.caseType as string) || 'lowercase';

    switch (caseType) {
      case 'lowercase':
        return input.toLowerCase();
      
      case 'UPPERCASE':
        return input.toUpperCase();
      
      case 'Title Case':
        return capitalCase(input);
      
      case 'Sentence case':
        return sentenceCase(input);
      
      case 'camelCase':
        return camelCase(input);
      
      case 'PascalCase':
        return pascalCase(input);
      
      case 'snake_case':
        return snakeCase(input);
      
      case 'SCREAMING_SNAKE_CASE':
        return constantCase(input);
      
      case 'kebab-case':
        return kebabCase(input);
      
      case 'dot.case':
        return dotCase(input);
      
      default:
        return input;
    }
  },
};

